import { Injectable } from '@angular/core';
import { map as _map, round as _round } from 'lodash-es';

import {
  DoseRange,
  DoseRangeDto,
  DoseRangeInfo,
  DoseRangeInfoDto,
  DosingCalculatorVariableDosing,
  DosingCalculatorVariableDosingOverride,
  DosingCalculatorWeightBasedDoseRange,
  DosingCalculatorWeightBasedDoseRanges,
  GuidStrValue,
  SystemGuid,
  VariableDoseRangesDto,
  VariableDosingDto,
  VariableDosingOverrideDto,
  WeightBasedDoseRangeDto,
} from 'webcommon/shared';

import { NumUtil } from './NumUtil';
import { StringBuilder } from './StringBuilder';

// Copied From iMedica.Prm.BusinessObject.NDD_DoseRangeChecking and converted to js

// constants
const singleDose = 'single dose ';
const perDay = 'per day ';
const AdjustedForWeight = ' (adjusted for patient\'s weight)';
const MetricWeightMask = '0.000';
const AdjustedForBodySurfaceArea = ' (adjusted for body surface area)';
const maxPediatricAgeInDays = 6569;

@Injectable({
  providedIn: 'root',
})
export class DoseRangeUtil {
  static readonly ajsFactoryName = 'DoseRangeUtil';

  constructor(
    private readonly numUtil: NumUtil,
    private readonly systemGuid: SystemGuid,
  ) { }

  allowsBsaAdjustment(drc: DoseRangeDto, patientAgeInDays: number) {
    if (!drc) {
      return false;
    }

    patientAgeInDays = patientAgeInDays || 0;

    const result = drc.BodySurfaceAreaRequired ||
      (patientAgeInDays <= maxPediatricAgeInDays &&
      drc.WeightRequired &&
      this.canCalculateByMg(drc));

    return result;
  }

  canCalculateByMg(drc: DoseRangeDto) {
    const strengthUomAbbr = drc && drc.IngredientStrengthUomAbbr;

    if (!strengthUomAbbr) {
      return false;
    }

    const isMg = strengthUomAbbr.toLowerCase() === 'mg';
    return isMg;
  }

  createWeightBasedMaxMinInformation(weightBasedDoseRange: WeightBasedDoseRangeDto) {
    if (!weightBasedDoseRange) {
      return '';
    }

    const sb = new StringBuilder();

    this.addDoseRange(
      sb,
      weightBasedDoseRange.LowDoseAmount,
      weightBasedDoseRange.LowDoseAmountUnits,
      weightBasedDoseRange.HighDoseAmount,
      weightBasedDoseRange.HighDoseAmountUnits,
    );
    if (sb.Length > 0) {
      sb.AppendLine();
    }

    this.addDoseRange(
      sb,
      weightBasedDoseRange.LowDoseForm,
      weightBasedDoseRange.LowDoseFormUnits,
      weightBasedDoseRange.HighDoseForm,
      weightBasedDoseRange.HighDoseFormUnits,
    );
    if (sb.Length > 0) {
      sb.AppendLine();
    }

    this.addNTEInfo(sb, weightBasedDoseRange.NTEAmount, weightBasedDoseRange.NTEAmountUnits);
    this.addNTEInfo(sb, weightBasedDoseRange.NTEForm, weightBasedDoseRange.NTEFormUnits);
    this.addSingleDoseNTEInfo(sb, weightBasedDoseRange.NTESingleDoseAmount, weightBasedDoseRange.NTESingleDoseAmountUnits);

    return sb.ToString();
  }

  formatAgeInDays(ageInDays: number) {
    if (ageInDays < 60) {
      return this.numUtil.format(ageInDays / 7, '0') + ' weeks';
    }
    if (ageInDays < 730) {
      return this.numUtil.format(ageInDays / 30.5, '0') + ' months';
    }
    return this.numUtil.format(ageInDays / 365, '0') + ' years';
  }

  getWeightBasedSummary(weightBasedDosing: DosingCalculatorVariableDosing) {
    const sb = new StringBuilder();
    if (weightBasedDosing.minAge && weightBasedDosing.minAge > 0) {
      sb.Append('Age ');
      sb.Append(this.formatAgeInDays(0));
      sb.Append(' - ');
      sb.AppendLine(this.formatAgeInDays(weightBasedDosing.minAge - 1));
      sb.AppendLine('  Manual Input Required');
    }
    weightBasedDosing.overrides.forEach(doseRange => {
      sb.AppendLine();
      sb.Append('Age ');
      sb.Append(this.formatAgeInDays(doseRange.minAge || 0));
      sb.Append(' - ');
      sb.AppendLine(this.formatAgeInDays(doseRange.maxAge || 0));
      sb.Append('  ');
      sb.Append(doseRange.overrideDosingQuantity ? doseRange.overrideDosingQuantity : weightBasedDosing.defaultDosingQuantity);
      sb.Append(' ');
      sb.Append(weightBasedDosing.unitDescription);

      const maxDose = doseRange.overrideMaximumDoseQuantity ?? weightBasedDosing.defaultMaximumDoseQuantity;
      if (maxDose) {
        sb.AppendLine();
        sb.Append('  Maximum Dose ');
        sb.Append(maxDose);
        sb.Append(' ');
        sb.Append(weightBasedDosing.maxUnitDescription);
      }
      sb.AppendLine();
    });
    sb.AppendLine();
    sb.Append('Age ');
    sb.Append(this.formatAgeInDays((weightBasedDosing.maxAge || 0) + 1));
    sb.AppendLine(' and older');
    sb.Append('  Manual Input Required');
    return sb.ToString();
  }

  getReadonlyDoseRange(doseRangeDto: DoseRangeDto) {
    return new DoseRange(doseRangeDto);
  }

  getReadonlyDoseRangeInfo(doseRangeInfoDto: DoseRangeInfoDto) {
    return new DoseRangeInfo(doseRangeInfoDto);
  }

  mapVariableDosingFromDto(variableDosingDto: VariableDosingDto) {
    if (!variableDosingDto) return null;
    const variableDosing: DosingCalculatorVariableDosing = {
      isDisabled: false,
      minAge: variableDosingDto.MinAgeInDays,
      maxAge: variableDosingDto.MaxAgeInDays,
      defaultDosingQuantity: variableDosingDto.DefaultDosingQuantity,
      defaultMaximumDoseQuantity: variableDosingDto.DefaultMaximumDoseQuantity,
      variableDosingTypeId: variableDosingDto.VariableDosingTypeId,
      overrides: _map(variableDosingDto.Overrides, mapOverrideFromDto),
      calculationErrors: variableDosingDto.CalculationErrors,
    };
    const processedDosingType = this.processDosingType(variableDosing.variableDosingTypeId);
    variableDosing.unitDescription = processedDosingType.dosingUnits;
    variableDosing.maxUnitDescription = processedDosingType.maxDoseUnits;
    variableDosing.summary = this.getWeightBasedSummary(variableDosing);
    return variableDosing;

    function mapOverrideFromDto(override: VariableDosingOverrideDto) {
      const result: DosingCalculatorVariableDosingOverride = {
        minAge: override.MinAgeInDays,
        maxAge: override.MaxAgeInDays,
        overrideDosingQuantity: override.OverrideDosingQuantity,
        overrideMaximumDoseQuantity: override.OverrideMaximumDoseQuantity,
      };
      return result;
    }
  }

  mapVariableDosingToDto(variableDosing: DosingCalculatorVariableDosing) {
    if (!variableDosing || variableDosing.isDisabled) return null;
    const dto: VariableDosingDto = {
      DefaultDosingQuantity: variableDosing.defaultDosingQuantity,
      DefaultMaximumDoseQuantity: variableDosing.defaultMaximumDoseQuantity,
      MaxAgeInDays: variableDosing.maxAge,
      MinAgeInDays: variableDosing.minAge,
      Overrides: _map(variableDosing.overrides, mapOverrideToDto),
      VariableDosingTypeId: variableDosing.variableDosingTypeId,
      CalculationErrors: [],
    };
    return dto;

    function mapOverrideToDto(override: DosingCalculatorVariableDosingOverride) {
      const result: VariableDosingOverrideDto = {
        MaxAgeInDays: override.maxAge,
        MinAgeInDays: override.minAge,
        OverrideDosingQuantity: override.overrideDosingQuantity,
        OverrideMaximumDoseQuantity: override.overrideMaximumDoseQuantity,
      };
      return result;
    }
  }

  processDoseRanges(variableDoseRanges: VariableDoseRangesDto) {
    const sourceWeightBasedDoseRanges = variableDoseRanges.WeightBasedDoseRanges || [];
    const weightBasedDoseRanges = sourceWeightBasedDoseRanges.map(x => {
      const weightBasedDoseRange = x as DosingCalculatorWeightBasedDoseRange;
      weightBasedDoseRange.dosingQuantity = null;
      weightBasedDoseRange.maximumDoseQuantity = null;
      return weightBasedDoseRange;
    });
    let minAge = 365;
    let maxAge = 0;
    let variableDosingTypeId: GuidStrValue | undefined;

    weightBasedDoseRanges.forEach(doseRange => {
      const matches = doseRange.MaxMinInformation && doseRange.MaxMinInformation.match(/\r\n/g);
      const maxMinInformationRows = (matches || []).length + 1;
      doseRange.maxMinInformationRows = maxMinInformationRows;

      if (doseRange.MinAgeInDays < minAge) {
        minAge = doseRange.MinAgeInDays;
      }
      if (doseRange.MaxAgeInDays > maxAge) {
        maxAge = doseRange.MaxAgeInDays;
      }
      variableDosingTypeId = doseRange.VariableDosingTypeId;
    });

    const processedDosingType = this.processDosingType(variableDosingTypeId);

    const result: DosingCalculatorWeightBasedDoseRanges = {
      weightBasedDoseRanges,
      dosingUnits: processedDosingType.dosingUnits,
      maxAge,
      maxDoseUnits: processedDosingType.maxDoseUnits,
      minAge,
      variableDosingTypeId,
    };
    return result;
  }

  private processDosingType(variableDosingTypeId?: GuidStrValue) {
    let dosingUnits: string | undefined;
    let maxDoseUnits: string | undefined;

    switch (variableDosingTypeId) {
      case this.systemGuid.DosingCalculatorUnitGPerKgPerDay: {
        dosingUnits = 'g/kg/day';
        maxDoseUnits = 'g';
        break;
      }
      case this.systemGuid.DosingCalculatorUnitMcgPerKgPerDay: {
        dosingUnits = 'mcg/kg/day';
        maxDoseUnits = 'mcg';
        break;
      }
      case this.systemGuid.DosingCalculatorUnitMgPerKgPerDay: {
        dosingUnits = 'mg/kg/day';
        maxDoseUnits = 'mg';
        break;
      }
      case this.systemGuid.DosingCalculatorUnitMlPerKgPerDay: {
        dosingUnits = 'mL/kg/day';
        maxDoseUnits = 'mL';
        break;
      }
    }

    return {
      dosingUnits,
      maxDoseUnits,
    };
  }

  // this summary copied from PRM
  /// <summary>
  /// Returns >max/min warning message.
  /// </summary>
  /// <param name='amount'>amount is the dose in either mg or ml</param>
  /// <param name='frequencyPerDay'>number of times taken a day</param>
  /// <param name='durationOfTherapyInDays'>the duration in number days</param>
  /// <param name='drc'>found NDD_DoseRangeChecking</param>
  /// <returns>max/min warning message</returns>
  GetMaxMinWarningMsg(
    drc: DoseRangeDto,
    amount?: number | null,
    frequencyPerDay?: number | null,
    durationOfTherapyInDays?: number | null,
  ): string {
    if (!drc) {
      return '';
    }

    if (drc.ErrorMsg && -1 === drc.ErrorMsg.indexOf('there is no dosing information supplied for this patient')) {
      return drc.ErrorMsg;
    }

    amount = amount || 0;
    frequencyPerDay = frequencyPerDay || 0;
    durationOfTherapyInDays = durationOfTherapyInDays || 0;

    const dailyDose = this.dailyDosing(drc, amount, frequencyPerDay);

    if (drc.AdjustedLowDose !== null && drc.AdjustedLowDose > 0 && dailyDose < drc.AdjustedLowDose) {
      return 'The dosing is less than the recommended daily dosing; ' + this.fdbDosingInformation(drc);
    }
    else if (drc.AdjustedHighDose !== null && drc.AdjustedHighDose > 0 &&
      drc.AdjustedMaximumSingleDose !== null && drc.AdjustedMaximumSingleDose > 0 &&
      dailyDose > drc.AdjustedHighDose && dailyDose < drc.AdjustedMaximumSingleDose
    ) {
      return 'The dosing is greater than the recommended daily dosing; ' + this.fdbDosingInformation(drc);
    }
    else if (drc.AdjustedHighDose !== null && drc.AdjustedHighDose > 0 && dailyDose > drc.AdjustedHighDose) {
      return 'The dosing is greater than the recommended daily dosing; ' + this.fdbDosingInformation(drc);
    }
    else if ((drc.AdjustedMaximumDosePerDay !== null && drc.AdjustedMaximumDosePerDay > 0 && dailyDose > drc.AdjustedMaximumDosePerDay) ||
        (drc.AdjustedNteAmountPerSingleDose !== null && drc.AdjustedNteAmountPerSingleDose > 0
          && dailyDose > drc.AdjustedNteAmountPerSingleDose)
    ) {
      return 'The dosing is greater than the recommended maximum single dose; ' + this.fdbDosingInformation(drc);
    }
    else if (drc.LowFrequency !== null && drc.LowFrequency > 0 && drc.LowFrequency > frequencyPerDay) {
      // DRCM Warning Messages #8
      return 'Prescribed frequency of administration per day is ' +
        'less than the recommended minimum frequency of administration range for the drug.  Please evaluate frequency.';
    }
    else if (drc.HighFrequency !== null && drc.HighFrequency > 0 && frequencyPerDay > drc.HighFrequency) {
      // DRCM Warning Messages #9
      return 'Prescribed frequency of administration per day is ' +
        'greater than the recommended maximum frequency of administration for the drug.  Please evaluate frequency.';
    }
    else if (drc.LowDuration !== null && drc.LowDuration > 0 && durationOfTherapyInDays < drc.LowDuration) {
      // DRCM Warning Messages #10
      return 'Prescribed duration is less than the recommended low duration for the drug.';
    }
    else if (drc.HighDuration !== null && drc.HighDuration > 0 && durationOfTherapyInDays > drc.HighDuration){
      // DRCM Warning Messages #11
      return 'Prescribed duration is greater than the highest recommended duration for the drug.';
    }

    return '';
  }

  CreateMaxMinInformation(drc: DoseRangeDto, includeErrorMsg: boolean) {
    if (!drc) {
      return '';
    }

    const sb = new StringBuilder();

    if (includeErrorMsg && drc.ErrorMsg && (drc.ErrorMsg.length > 0)) {
      sb.AppendLine(drc.ErrorMsg);
      sb.AppendLine();
      sb.AppendLine();
    }

    let doseLow = 0;
    let doseHigh = 0;
    let doseFormLow = 0;
    let doseFormHigh = 0;

    let doseUnit = '';
    const doseResult = this.getHighLowDose(drc);
    doseLow = doseResult.doseLow;
    doseHigh = doseResult.doseHigh;

    const formDoseResult = this.getHighLowFormDose(drc);
    doseFormLow = formDoseResult.doseFormLow;
    doseFormHigh = formDoseResult.doseFormHigh;

    if (doseLow > 0 && doseHigh > 0) {
      sb.Append(this.fdbDosingInformation(drc));
      sb.AppendLine();
      sb.AppendLine();

      if (drc.RangeLowDoseUnits) {
        doseUnit = drc.RangeLowDoseUnits;
        doseUnit = this.doseUnitOnly(doseUnit);
      } else {
        doseUnit = '';
      }

      sb.Append('dose ');
      sb.Append(this.numUtil.format(doseLow, '[0].[00]'));
      sb.Append(' ');
      sb.Append(doseUnit);

      if (drc.RangeHighDoseUnits) {
        doseUnit = drc.RangeHighDoseUnits;
        doseUnit = this.doseUnitOnly(doseUnit);
      } else {
          doseUnit = '';
      }

      sb.Append(' to ');
      sb.Append(this.numUtil.format(doseHigh, '[0].[00]'));
      sb.Append(' ');
      sb.Append(doseUnit);
      if (drc.WeightRequired) {
        sb.Append(AdjustedForWeight);
      }
      if (drc.BodySurfaceAreaRequired) {
        sb.Append(AdjustedForBodySurfaceArea);
      }
    }

    if (doseFormLow > 0 && doseFormHigh > 0) {
      if (drc.RangeLowDoseFormUnits) {
        doseUnit = drc.RangeLowDoseFormUnits;
        doseUnit = this.doseUnitOnly(doseUnit);
      }
      else {
        doseUnit = '';
      }

      sb.AppendLine();
      sb.Append('dose form ');
      sb.Append(this.numUtil.format(doseFormLow, '[0].[00]'));
      sb.Append(' ');
      sb.Append(doseUnit);

      if (drc.RangeHighDoseFormUnits) {
        doseUnit = drc.RangeHighDoseFormUnits;
        doseUnit = this.doseUnitOnly(doseUnit);
      }
      else {
        doseUnit = '';
      }

      sb.Append(' to ');
      sb.Append(this.numUtil.format(doseFormHigh, '[0].[00]'));
      sb.Append(' ');
      sb.Append(doseUnit);

      if (drc.WeightRequired) {
        sb.Append(AdjustedForWeight);
      }
      if (drc.BodySurfaceAreaRequired) {
        sb.Append(AdjustedForBodySurfaceArea);
      }
    }

    return sb.ToString();
  }

  CalculateDosingInformation(drc: DoseRangeDto, frequencyPerDay?: number, mg?: number) {
    if (!drc) {
      return '';
    }

    frequencyPerDay = frequencyPerDay || 0;
    mg = mg || 0;

    if (mg > 0) {
      if (this.isMilliliterUnitVolume(drc)) {
        return this.calculateMLFromMGDosing(drc, frequencyPerDay, mg);
      }
      else {
        return this.calculateMGDosing(drc, mg);
      }
    }

    return '';
  }

  private isMilliliterUnitVolume(drc: DoseRangeDto) {
    if (!drc) {
      return false;
    }

    if (drc.IngredientVolumeUomAbbr &&
      drc.IngredientVolumeUomAbbr.toLowerCase() === 'ml' &&
      drc.IngredientVolume !== null &&
      drc.IngredientVolume > 0
    ) {
      return true;
    }

    return false;
  }

  private dailyDosing(drc: DoseRangeDto, amount?: number, frequencyPerDay?: number) {
    if (!drc) {
      return 0;
    }
    amount = amount || 0;
    frequencyPerDay = frequencyPerDay || 0;

    let dosingValue = 0;
    const dosePerDay = this.numUtil.multiply(amount, frequencyPerDay);
    let strength = 0;
    if (drc.IngredientStrength !== null && drc.IngredientStrength >= 0) {
      strength = drc.IngredientStrength;
    }

    if (this.isMilliliterUnitVolume(drc)) {
      dosingValue = this.numUtil.multiply(strength, this.numUtil.divide(amount, drc.IngredientVolume));
    }
    else {
      dosingValue = this.numUtil.multiply(strength, dosePerDay);
    }

    return dosingValue;
  }

  private addDoseRange(
    sb: StringBuilder,
    low: number | null,
    lowUnits: string | null,
    high: number | null,
    highUnits: string | null,
  ) {
    if (!sb) {
      return;
    }

    if (low && low > 0 && high && high > 0) {
      sb.Append(this.numUtil.format(low, '0.00'));

      if (lowUnits) {
        sb.Append(' ');
        sb.Append(lowUnits);
      }
      sb.Append(' - ');
      sb.Append(this.numUtil.format(high, '0.00'));

      if (highUnits) {
        sb.Append(' ');
        sb.Append(highUnits);
      }
    }
  }

  private addNTEInfo(sb: StringBuilder, nte: number | null, nteUnits: string | null) {
    if (!sb) {
      return;
    }

    if (nte && nte > 0) {
      sb.Append('NTE: ');
      sb.Append(this.numUtil.format(nte, '0.00'));
      if (nteUnits) {
        sb.Append(' ');
        sb.Append(nteUnits);
      }
      sb.AppendLine();
    }
  }

  private addSingleDoseNTEInfo(sb: StringBuilder, nte: number | null, nteUnits: string | null) {
    if (!sb) {
      return;
    }

    if (nte && nte > 0) {
      sb.Append('Single Dose NTE: ');
      sb.Append(this.numUtil.format(nte, '0.00'));
      if (nteUnits) {
        sb.Append(' ');
        sb.Append(nteUnits);
      }
      sb.AppendLine();
    }
  }

  private doseFormRange(drc: DoseRangeDto, sb: StringBuilder) {
    if (!drc || !sb) {
      return;
    }

    this.addDoseRange(sb, drc.RangeLowDoseForm, drc.RangeLowDoseFormUnits, drc.RangeHighDoseForm, drc.RangeHighDoseFormUnits);
  }

  private doseRange(drc: DoseRangeDto, sb: StringBuilder) {
    if (!drc || !sb) {
      return;
    }

    this.addDoseRange(sb, drc.RangeLowDose, drc.RangeLowDoseUnits, drc.RangeHighDose, drc.RangeHighDoseUnits);
  }

  private fdbDosingInformation(drc: DoseRangeDto) {
    const sb = new StringBuilder();

    if (this.isMilliliterUnitVolume(drc)) {
      this.doseFormRange(drc, sb);
      if (sb.Length > 0) {
        sb.Append('; ');
      }
      this.doseRange(drc, sb);
    } else {
      this.doseRange(drc, sb);
      if (sb.Length > 0) {
        sb.Append('; ');
      }
      this.doseFormRange(drc, sb);
    }

    return sb.ToString();
  }

  /// <summary>
  /// This method gets the doselow and the dosehigh values from the dosing row and corrects
  /// the doselow and dosehigh values by the either the patient's weight or body surface area
  /// as required.
  /// </summary>
  /// <param name='drc'>NDD_DoseRangeChecking row selected for the patient's age</param>
  /// <param name='doseLow'>returns the the corrected doseLow value</param>
  /// <param name='doseHigh'>returns the the corrected doseHigh value</param>
  private getHighLowDose(drc: DoseRangeDto) {
    const result = {
      doseLow: 0,
      doseHigh: 0,
    };

    if (!drc) {
      return result;
    }

    if (drc.RangeLowDose !== null && drc.RangeLowDose >= 0 && drc.RangeHighDose !== null && drc.RangeHighDose >= 0) {
      result.doseLow = drc.RangeLowDose;
      result.doseHigh = drc.RangeHighDose;
    }

    if (drc.WeightRequired && drc.WeightKg !== null && drc.WeightKg >= 0) {
      result.doseLow = this.numUtil.multiply(result.doseLow, Math.max(1, drc.WeightKg));
      result.doseHigh = this.numUtil.multiply(result.doseHigh, Math.max(1, drc.WeightKg));
    }

    if (drc.BodySurfaceAreaRequired && drc.BodySurfaceArea !== null && drc.BodySurfaceArea >= 0) {
      const bsa = drc.BodySurfaceArea;
      result.doseLow = this.numUtil.divide(this.numUtil.multiply(result.doseLow, bsa), 1.73);
      result.doseHigh = this.numUtil.divide(this.numUtil.multiply(result.doseHigh, bsa), 1.73);
    }

    return result;
  }

  /// <summary>
  /// This method gets the doseformlow and the doseformhigh values from the dosing row
  /// and corrects the doseformlow and doseformhigh values by the either the patient's
  /// weight or body surface area as required.
  /// </summary>
  /// <param name='drc'>NDD_DoseRangeChecking row selected for the patient's age</param>
  /// <param name='doseFormLow'>returns the the corrected doseFormLow value</param>
  /// <param name='doseFormHigh'>returns the the corrected doseFormHigh value</param>
  private getHighLowFormDose(drc: DoseRangeDto) {
    const result = {
      doseFormLow: 0,
      doseFormHigh: 0,
    };

    if (!drc) {
      return result;
    }

    if (drc.RangeLowDoseForm !== null && drc.RangeLowDoseForm >= 0 && drc.RangeHighDoseForm !== null && drc.RangeHighDoseForm >= 0) {
      result.doseFormLow = drc.RangeLowDoseForm;
      result.doseFormHigh = drc.RangeHighDoseForm;
    }

    if (drc.WeightRequired && drc.WeightKg !== null && drc.WeightKg >= 0) {
      result.doseFormLow = this.numUtil.multiply(result.doseFormLow, Math.max(1, drc.WeightKg));
      result.doseFormHigh = this.numUtil.multiply(result.doseFormHigh, Math.max(1, drc.WeightKg));
    }

    if (drc.BodySurfaceAreaRequired && drc.BodySurfaceArea !== null && drc.BodySurfaceArea >= 0) {
      const bsa = drc.BodySurfaceArea;
      result.doseFormLow = this.numUtil.divide(this.numUtil.multiply(result.doseFormLow, bsa), 1.73);
      result.doseFormHigh = this.numUtil.divide(this.numUtil.multiply(result.doseFormHigh, bsa), 1.73);
    }

    return result;
  }

  private doseUnitOnly(doseUnit: string) {
    if (!doseUnit) {
      return doseUnit;
    }

    const len = doseUnit.indexOf('/');

    if (len > 0) {
      doseUnit = doseUnit.substr(0, len);
    }

    return doseUnit;
  }

  private dosingUnitMilliliter(drc: DoseRangeDto, frequencyPerDay?: number, dose?: number) {
    const sb = new StringBuilder();
    frequencyPerDay = frequencyPerDay || 0;
    dose = dose || 0;

    if (this.isMilliliterUnitVolume(drc)) {
      let timesPerDay = frequencyPerDay;

      if (frequencyPerDay === 0) {
        sb.AppendLine('Cannot calculate a single dose without a frequency.');
        timesPerDay = 1;
      }

      // do milliliter per day
      const convertDose = _round(this.numUtil.divide(dose, timesPerDay), 1);
      if (convertDose !== 0) {
        sb.AppendLine();
        sb.Append('Milliliter(s) ');

        if (frequencyPerDay > 0) {
          sb.Append(singleDose);
          sb.Append(this.numUtil.format(convertDose, '[0].[0]'));
        } else {
          sb.Append(perDay);
          sb.Append(this.numUtil.format(this.numUtil.multiply(convertDose, timesPerDay), '[0].[0]'));
        }
      }
    }

    return sb.ToString();
  }

  private formatEach(dose: number | null) {
    dose = dose || 0;

    let result = '';

    const remainder = this.numUtil.subtract(dose, Math.trunc(dose));

    if (remainder > 0.499 && remainder < 0.999) {
      result = ' 1/2';
    }

    result = this.numUtil.format(Math.trunc(dose), '[0]') + result;
    return result;
  }

  private dosingUnitMilligram(mg: number | null, strength: number | null) {
    mg = mg || 0;
    strength = strength || 0;

    const sb = new StringBuilder();

    // do milligram per dose
    let convertDose = Math.floor(mg);
    if (convertDose !== 0) {
      sb.Append('Milligram(s) ');
      sb.Append(singleDose);
      sb.Append(this.numUtil.format(convertDose, '[0]'));
    }

    // do tables per dose
    convertDose = _round(mg, 3);
    const formatted = this.formatEach(this.numUtil.divide(convertDose, strength));
    if (formatted) {
      sb.AppendLine();
      sb.Append('Each ');
      sb.Append(singleDose);
      sb.Append(formatted);
    }

    return sb.ToString();
  }

  private calculateMLFromMGDosing(drc: DoseRangeDto, frequencyPerDay: number | null, mg: number | null) {
    if (!drc) {
      return '';
    }

    frequencyPerDay = frequencyPerDay || 0;
    mg = mg || 0;

    const sb = new StringBuilder();

    if (drc.WeightRequired && drc.WeightKg !== null && drc.WeightKg >= 0) {
      sb.Append(this.numUtil.format(this.numUtil.multiply(mg, drc.WeightKg), '0.0'));
      sb.Append(' mg ');
      sb.AppendLine(AdjustedForWeight);
      sb.AppendLine();
    }

    if (mg > 0 &&
      drc.IngredientVolume && drc.IngredientVolume > 0 &&
      drc.IngredientStrength && drc.IngredientStrength > 0
    ) {
        const ml = this.numUtil.divide(drc.IngredientVolume, drc.IngredientStrength);
        let totalML = 0;

        if (drc.WeightRequired && drc.WeightKg !== null && drc.WeightKg >= 0) {
          totalML = this.numUtil.multiply(ml, mg, drc.WeightKg);
        }
        else {
          totalML = this.numUtil.multiply(ml, mg);
        }
        sb.Append(this.dosingUnitMilliliter(drc, frequencyPerDay, totalML));

        // add calculation information
        sb.AppendLine();
        sb.Append('Daily total ml: ');
        sb.Append(this.numUtil.format(totalML, '0.0'));
        if (drc.WeightRequired) {
          sb.Append(' = ((');
        } else {
          sb.Append(' = (');
        }

        sb.Append(this.numUtil.format(ml, '0.0[000]'));
        sb.Append(' (ml per mg) * ');
        sb.Append(this.numUtil.format(mg, '0.0'));

        if (drc.WeightRequired) {
          sb.Append(') * ');
          sb.Append(this.numUtil.format(drc.WeightKg || 0, MetricWeightMask));
          sb.Append('(kg))');
        } else {
          sb.Append(')');
        }
    }

    return sb.ToString();
  }

  private calculateMGDosing(drc: DoseRangeDto, mg: number | null) {
    if (!drc) {
      return '';
    }

    mg = mg || 0;

    const sb = new StringBuilder();

    let bsa = 0;
    let adjustedMG = mg;
    let strength = 0;
    if (drc.IngredientStrength !== null && drc.IngredientStrength >= 0) {
      strength = drc.IngredientStrength;
    }

    if (drc.WeightRequired) {
      adjustedMG = this.numUtil.multiply(mg, (drc.WeightKg || 0));
      sb.Append(this.numUtil.format(adjustedMG, '0.0'));
      sb.Append(' mg ');
      sb.AppendLine(AdjustedForWeight);
      sb.AppendLine();
    } else if (drc.BodySurfaceAreaRequired && drc.BodySurfaceArea !== null && drc.BodySurfaceArea >= 0) {
      bsa = drc.BodySurfaceArea;
      adjustedMG = this.numUtil.divide(this.numUtil.multiply(bsa, mg), 1.73);
      sb.Append(this.numUtil.format(adjustedMG, '0.0'));
      sb.Append(' mg ');
      sb.AppendLine(AdjustedForBodySurfaceArea);
      sb.AppendLine();
    }

    sb.Append(this.dosingUnitMilligram(adjustedMG, strength));

    // added calculation information
    if (drc.WeightRequired) {
      sb.AppendLine();
      sb.AppendLine();
      sb.Append('Single dose mg: ');
      sb.Append(this.numUtil.format(adjustedMG, '0.0'));
      sb.Append(' = (');
      sb.Append(this.numUtil.format(mg, '0.0'));
      sb.Append('(mg) * ');
      sb.Append(this.numUtil.format(drc.WeightKg || 0, MetricWeightMask));
      sb.Append('(kg))');
    }
    else if (drc.BodySurfaceAreaRequired) {
      // add calculation information
      sb.AppendLine();
      sb.AppendLine();
      sb.Append('Single pediatric dose mg: ');
      sb.Append(this.numUtil.format(adjustedMG, '0.0'));
      sb.Append(' = ((');
      sb.Append(this.numUtil.format(bsa, '0.0[000]'));
      sb.Append(' (bsa m2) * ');
      sb.Append(this.numUtil.format(mg, '0.0'));
      sb.Append(' (adult dose mg)');
      sb.AppendLine(') / 1.73)');
      sb.AppendLine();
      sb.Append('Body Surface Area is calculated using the \'DuBois and DuBois 2\' formula.');
    }

    return sb.ToString();
  }
}
